import { nextTick } from 'vue';
import { useMainStore } from '../stores/mainStore.js';
import { isClient, usePreferredReducedMotion } from '@vueuse/core';

const ANCHOR_WAIT_INTERVAL_MS = 100;
const ANCHOR_WAIT_TIMEOUT_MS = 10000;
const IMAGE_WAIT_TIMEOUT_MS = 6000;

// Same-hash guard: if scrollToAnchor is already in-flight for a given hash
// (e.g. the middleware and pageLoaded both fire), the second call exits immediately
// instead of setting up a duplicate scroll + observer loop.
let pendingScrollHash = null;

// Holds the cleanup function returned by the active keepAnchored call so that
// navigating to a different anchor cancels the previous observer before starting
// a new one.
let cancelActiveKeepAnchored = null;

export const useAnchorTag = () => {
  const mainStore = useMainStore();
  const hasStickyHeader = mainStore.theme?.header?.options?.sticky_header || false;
  const prefersReducedMotion = isClient ? usePreferredReducedMotion() : { value: 'no-preference' };

  /**
   * Retrieves the height of the header using the 'view-header' ID set in default.vue.
   * If the element does not exist, it returns 0.
   *
   * @returns {number} The height of the header element in pixels, or 0 if the element is not found.
   */
  const getStickyHeaderHeight = () => {
    const headerWrapper = document.querySelector('#view-header');
    return headerWrapper?.getBoundingClientRect().height ?? 0;
  };

  /**
   * Validates if the current route is an anchor route.
   * It checks if the paths are the same and the hashes are different.
   * If the hashes are the same, it checks if the hash is an anchor.
   * @returns {boolean} Whether the current route is an anchor route.
   */
  const validateAnchorRoute = (to, from) => {
    const isSamePath = to.path === from.path;
    const isDifferentHash = to.hash !== from.hash;
    const isSameHashWithAnchor = to.hash && to.hash === from.hash;
    return isSamePath && (isDifferentHash || isSameHashWithAnchor);
  };

  /**
   * Finds the element targeted by the route hash.
   * @param {string} hash The route hash (e.g. `#faqs`).
   * @returns {Element|null} The matching element, or null if none is found.
   */
  const findAnchorElement = (hash) => {
    try {
      let targetElement = document.querySelector(hash);

      if (!targetElement) {
        const anchorId = hash.slice(1);
        targetElement = document.querySelector(`[data-anchor-tag="${anchorId}"]`);
      }

      return targetElement;
    } catch {
      return null;
    }
  };

  /**
   * Waits until the anchor element appears in the DOM, polling up to a timeout.
   *
   * @param {string} hash The route hash to wait for.
   * @returns {Promise<Element|null>}
   */
  const waitForAnchorElement = async (hash) => {
    const deadline = Date.now() + ANCHOR_WAIT_TIMEOUT_MS;

    let element = findAnchorElement(hash);
    while (!element && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, ANCHOR_WAIT_INTERVAL_MS));
      element = findAnchorElement(hash);
    }

    return element;
  };

  /**
   * Resolves after the next animation frame, falling back to a timeout when
   * `requestAnimationFrame` is unavailable (e.g. some test environments).
   * @returns {Promise<void>}
   */
  const nextFrame = () =>
    new Promise((resolve) => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => resolve());
        return;
      }
      setTimeout(resolve, 16);
    });

  /**
   * Waits for an initial layout pass before measuring the scroll position.
   *
   * @returns {Promise<void>}
   */
  const waitForLayoutStable = async () => {
    await nextTick();

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    await nextFrame();
    await nextFrame();
  };

  /**
   * Computes the absolute document scroll position that places the target just
   * below the sticky header.
   * @param {Element} element The anchor element.
   * @returns {number} The target `scrollTop` in pixels.
   */
  const getTargetScrollTop = (element) => {
    const headerHeight = hasStickyHeader ? getStickyHeaderHeight() : 0;
    return element.getBoundingClientRect().top + window.scrollY - headerHeight;
  };

  /**
   * Keeps the viewport pinned to the anchor while late-loading images shift the
   * page layout after the initial scroll. Stops automatically once all images
   * have finished loading (or after a timeout), or immediately if the user
   * scrolls manually.
   *
   * @param {string} hash The route hash to stay anchored to.
   * @returns {() => void} A cleanup function that cancels the loop early — used
   *   by `scrollToAnchor` to tear down a previous anchor's repinning before
   *   starting a new scroll.
   */
  const keepAnchored = (hash) => {
    if (typeof ResizeObserver === 'undefined') return () => {};

    let isActive = true;

    const repin = () => {
      if (!isActive) return;
      const element = findAnchorElement(hash);
      if (!element) return;
      window.scrollTo({ top: getTargetScrollTop(element), behavior: 'auto' });
    };

    const observer = new ResizeObserver(repin);

    const cleanup = () => {
      if (!isActive) return;
      isActive = false;
      observer.disconnect();
      window.removeEventListener('wheel', cleanup);
      window.removeEventListener('touchmove', cleanup);
      window.removeEventListener('keydown', cleanup);
    };

    observer.observe(document.querySelector('#page') || document.body);

    // `scroll` is intentionally omitted: our own `scrollTo` would cancel the loop.
    window.addEventListener('wheel', cleanup, { passive: true });
    window.addEventListener('touchmove', cleanup, { passive: true });
    window.addEventListener('keydown', cleanup);

    // Stop repinning once all in-flight images have loaded rather than after a
    // fixed timeout — pages with heavy content can take longer than any constant.
    const pending = Array.from(document.images).filter((img) => !img.complete);
    if (!pending.length) {
      cleanup();
      return cleanup;
    }

    Promise.race([
      Promise.all(
        pending.map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            }),
        ),
      ),
      new Promise((resolve) => setTimeout(resolve, IMAGE_WAIT_TIMEOUT_MS)),
    ]).then(cleanup);

    return cleanup;
  };

  /**
   * Scrolls the viewport to the element identified by `hash`.
   *
   * A module-level guard ensures that if a concurrent call arrives for the same
   * hash (e.g. the middleware and the pageLoaded event firing close together),
   * the second call exits immediately so only one scroll loop runs at a time.
   * Navigating to a different hash first cancels any active `keepAnchored` loop
   * (its ResizeObserver, event listeners, and pending image timer) before
   * starting a new one.
   *
   * After locating the element the function waits for `waitForLayoutStable`
   * (nextTick + fonts ready + two animation frames) before measuring the scroll
   * position, then hands off to `keepAnchored` which re-pins the viewport as
   * late-loading images shift the layout.
   *
   * @param {string} hash The route hash to scroll to (e.g. `'#faqs'`).
   * @returns {Promise<void>} Resolves once the initial scroll has been issued.
   *   `keepAnchored` continues running in the background until images settle.
   */
  const scrollToAnchor = async (hash) => {
    if (!isClient || !hash) return;
    if (pendingScrollHash === hash) return;

    cancelActiveKeepAnchored?.();
    cancelActiveKeepAnchored = null;
    pendingScrollHash = hash;
    try {
      const targetElement = await waitForAnchorElement(hash);
      if (pendingScrollHash !== hash) return;
      if (!targetElement) return;

      await waitForLayoutStable();
      if (pendingScrollHash !== hash) return;

      const element = findAnchorElement(hash);
      if (!element) return;

      window.scrollTo({
        top: getTargetScrollTop(element),
        behavior: prefersReducedMotion.value === 'no-preference' ? 'smooth' : 'auto',
      });

      cancelActiveKeepAnchored = keepAnchored(hash);
    } catch (error) {
      console.warn('Failed to scroll to anchor:', error);
    } finally {
      if (pendingScrollHash === hash) pendingScrollHash = null;
    }
  };

  return {
    validateAnchorRoute,
    scrollToAnchor,
  };
};
